import { useEffect, useRef, useState } from "react";
import { Copy, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, download, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Shared";

const Card = ({ title, desc, children, testid }) => (
  <section data-testid={testid} className="rounded-xl border bg-card p-5 shadow-sm">
    <h2 className="font-heading text-lg font-semibold">{title}</h2>
    {desc && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

const Pick = ({ label, value, onChange, options, testid }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-72"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const cell = (v, dec) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return dec === "," ? String(v).replace(".", ",") : String(v);
  return String(v).replace(/[\t\r\n]+/g, " ").trim();
};

const toTsv = (rows, dec) => rows.map((r) => r.map((v) => cell(v, dec)).join("\t")).join("\n");

function TabBlock({ t, dec }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(toTsv(t.rows, dec)); toast.success(`${t.rows.length} baris ${t.label} disalin, tempel di tab ${t.tab}`); }
    catch { toast.error("Browser menolak akses clipboard"); }
  };
  return (
    <div data-testid={`upload-tab-${t.tab}`} className="rounded-xl border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold">{t.label} · {t.periode}</h3>
          <p className="text-xs text-muted-foreground">Tab <b>{t.tab}</b> · {t.rows.length} baris · {t.header.length} kolom (A–{String.fromCharCode(64 + t.header.length)})</p>
        </div>
        <Button data-testid={`upload-copy-${t.tab}`} onClick={copy}><Copy className="mr-1.5 h-4 w-4" />Salin data</Button>
      </div>
      <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
        <li>Di tab <b>{t.tab}</b>, hapus dulu baris lama <b>{t.bulan} {t.tahun}</b> untuk UP3: {t.units.join(", ")}.</li>
        <li>Klik sel <b>kolom A</b> pada baris kosong pertama, lalu tekan <b>Ctrl+V</b>.</li>
        {t.formula_col && <li>Jangan isi kolom {t.formula_col} — itu kolom rumus dan terisi otomatis.</li>}
      </ol>
      <div className="mt-3 max-h-56 overflow-auto rounded-lg border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card"><tr className="border-b text-left text-muted-foreground">{t.header.map((h) => <th key={h} className="whitespace-nowrap p-1.5">{h}</th>)}</tr></thead>
          <tbody>
            {t.rows.slice(0, 8).map((r, i) => <tr key={i} className="border-b">{r.map((v, j) => <td key={j} className="max-w-[16rem] truncate whitespace-nowrap p-1.5 font-num">{cell(v, dec)}</td>)}</tr>)}
          </tbody>
        </table>
      </div>
      {t.rows.length > 8 && <p className="mt-1 text-[11px] text-muted-foreground">Pratinjau 8 dari {t.rows.length} baris — tombol Salin menyalin semuanya.</p>}
    </div>
  );
}

function Result({ batch }) {
  const [jenis, setJenis] = useState("ALL");
  const [dec, setDec] = useState(() => localStorage.getItem("upload-dec") || ",");
  const [tabs, setTabs] = useState(null);
  const hasRpt = batch.files.some((f) => f.tab === "RPT_RCT");
  useEffect(() => {
    setTabs(null);
    api.get(`/upload/${batch.id}/tabs`, { params: { rpt_jenis: jenis } }).then((r) => setTabs(r.data.tabs)).catch((e) => toast.error(errMsg(e)));
  }, [batch.id, jenis]);
  const setD = (v) => { setDec(v); localStorage.setItem("upload-dec", v); };
  return (
    <Card testid="upload-result" title="2. Salin ke Google Sheet" desc="Tiap blok sudah sesuai urutan kolom tab Google Sheet. Salin, lalu tempel di baris kosong pertama tab yang sesuai.">
      <ul className="mb-4 space-y-1 text-sm">
        {batch.files.map((f) => (
          <li key={f.name} data-testid="upload-file-row"><b>{f.name}</b> → {f.label} {f.periode.join(", ")} · {f.rows ? `${f.rows} baris masuk` : <span className="text-amber-600">tidak ada baris masuk (semua UP3 di luar daftar)</span>}
            {f.skipped_rows > 0 && <span className="text-muted-foreground"> · {f.skipped_rows} baris dibuang ({f.skipped_units.join(", ")})</span>}
          </li>
        ))}
      </ul>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        {hasRpt && <Pick testid="upload-rpt-jenis" label="File RPT RCT masuk sebagai" value={jenis} onChange={setJenis} options={[["ALL", "RPT ALL & RCT ALL"], ["NON CT", "RPT NON CT & RCT NON CT"]]} />}
        <Pick testid="upload-decimal" label="Format angka desimal" value={dec} onChange={setD} options={[[",", "Koma (71,8) — Sheet Indonesia"], [".", "Titik (71.8) — Sheet English"]]} />
        <Button data-testid="upload-excel-button" variant="outline" onClick={() => download(`/upload/${batch.id}/excel`, { rpt_jenis: jenis }, "Data_Siap_Tempel.xlsx").catch((e) => toast.error(errMsg(e)))}>
          <FileSpreadsheet className="mr-1.5 h-4 w-4" />Unduh Excel
        </Button>
      </div>
      {!tabs ? <Loader2 className="h-5 w-5 animate-spin" /> : <div className="space-y-4">{tabs.map((t) => <TabBlock key={`${t.tab}-${t.periode}`} t={t} dec={dec} />)}</div>}
    </Card>
  );
}

export default function UploadPage() {
  const ref = useRef(null);
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = async (list) => {
    if (!list?.length) return;
    const fd = new FormData();
    [...list].forEach((f) => fd.append("files", f));
    setBusy(true); setBatch(null);
    try { const { data } = await api.post("/upload/parse", fd, { timeout: 120000 }); setBatch(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader eyebrow="Administrasi" title="Upload Data" desc="Unggah file export (YO Bulanan, RPT RCT, Rating Negatif, Lapor Ulang). Kolom dirapikan dan hanya UP3 yang diizinkan (atur di Pengaturan) yang masuk, siap disalin ke Google Sheet." />
      <Card testid="upload-drop-card" title="1. Pilih File" desc="Boleh beberapa file sekaligus. Jenis file dikenali otomatis.">
        <label data-testid="upload-dropzone" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files); }}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center transition-colors hover:border-[#F5A623] hover:bg-muted/40">
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6 text-muted-foreground" />}
          <span className="text-sm font-medium">{busy ? "Membaca file…" : "Klik atau seret file .xlsx ke sini"}</span>
          <input data-testid="upload-file-input" ref={ref} type="file" accept=".xlsx" multiple className="hidden" onChange={(e) => pick(e.target.files)} />
        </label>
      </Card>
      {batch && <Result batch={batch} />}
    </div>
  );
}
